import { EXPERIMENT_PREVIEW_URL } from "../config/previewUrl";

type PreviewMessage = Record<string, unknown>;
type PreviewFrameState = {
  ready: boolean;
  queuedMessages: PreviewMessage[];
};

const IMMEDIATE_MESSAGE_DELAYS_MS = [0, 250, 800];
const LOAD_MESSAGE_DELAYS_MS = [200, 700, 1500];
const READY_MESSAGE_TYPE = "falcon-tagger-ready";
const PREVIEW_RUNTIME_MESSAGE_TYPES = new Set([
  READY_MESSAGE_TYPE,
  "element-hover",
  "element-hover-end",
  "elements-in-rect-response",
  "iframe-scroll",
  "iframe-url-change",
]);
const previewFrameStateMap = new WeakMap<HTMLIFrameElement, PreviewFrameState>();

function getPreviewFrameState(frame: HTMLIFrameElement): PreviewFrameState {
  const existingState = previewFrameStateMap.get(frame);

  if (existingState) {
    return existingState;
  }

  const nextState: PreviewFrameState = {
    ready: true,
    queuedMessages: [],
  };
  previewFrameStateMap.set(frame, nextState);
  return nextState;
}

function markPreviewFramePending(frame: HTMLIFrameElement): void {
  const frameState = getPreviewFrameState(frame);
  frameState.ready = false;
  frameState.queuedMessages = [];
}

function sendMessageToFrame(
  frame: HTMLIFrameElement,
  message: PreviewMessage,
): void {
  const frameWindow = frame.contentWindow;
  if (!frameWindow) {
    return;
  }

  frameWindow.postMessage(message, getPreviewFrameOrigin(frame) ?? "*");
}

function flushQueuedMessages(frame: HTMLIFrameElement): void {
  const frameState = getPreviewFrameState(frame);
  if (!frameState.ready || frameState.queuedMessages.length === 0) {
    return;
  }

  const queuedMessages = [...frameState.queuedMessages];
  frameState.queuedMessages = [];

  for (const queuedMessage of queuedMessages) {
    sendMessageToFrame(frame, queuedMessage);
  }
}

function markPreviewFrameReady(frame?: HTMLIFrameElement | null): void {
  if (!frame) {
    return;
  }

  const frameState = getPreviewFrameState(frame);
  frameState.ready = true;
  flushQueuedMessages(frame);
}

export function getPreviewFrameOrigin(
  frame?: HTMLIFrameElement | null,
): string | null {
  const rawSrc = frame?.src || EXPERIMENT_PREVIEW_URL;

  if (!rawSrc) {
    return null;
  }

  try {
    return new URL(rawSrc, window.location.href).origin;
  } catch {
    return null;
  }
}

export function isMessageFromPreviewFrame(
  event: MessageEvent,
  frame?: HTMLIFrameElement | null,
): boolean {
  const frameWindow = frame?.contentWindow;
  const previewOrigin = getPreviewFrameOrigin(frame);
  const runtimeMessageType = String(event.data?.type ?? "");
  const sourceMatches = !frameWindow || !event.source || event.source === frameWindow;
  const originMatches =
    previewOrigin && event.origin ? event.origin === previewOrigin : null;
  const isMatchingFrame = originMatches ?? sourceMatches;

  if (
    isMatchingFrame &&
    frame &&
    PREVIEW_RUNTIME_MESSAGE_TYPES.has(runtimeMessageType)
  ) {
    markPreviewFrameReady(frame);
  }

  return isMatchingFrame;
}

export function postToPreviewFrame(
  frame: HTMLIFrameElement | null | undefined,
  message: PreviewMessage,
  options?: {
    queueUntilReady?: boolean;
  },
): void {
  if (!frame) {
    return;
  }

  const frameState = getPreviewFrameState(frame);
  if (options?.queueUntilReady && !frameState.ready) {
    frameState.queuedMessages.push(message);
    return;
  }

  sendMessageToFrame(frame, message);
}

export function schedulePreviewMessage(
  frame: HTMLIFrameElement,
  message: PreviewMessage,
  delays: number[],
): void {
  for (const delay of delays) {
    window.setTimeout(
      () => postToPreviewFrame(frame, message, { queueUntilReady: true }),
      delay,
    );
  }
}

export function scrollPreviewBy(
  frame: HTMLIFrameElement | null | undefined,
  deltaX: number,
  deltaY: number,
): void {
  if (!frame) {
    return;
  }

  postToPreviewFrame(frame, {
    type: "iframe-scroll-by",
    deltaX,
    deltaY,
  });
}

export function scrollPreviewToElement(
  frame: HTMLIFrameElement | null | undefined,
  selector?: string | null,
): void {
  if (!frame || !selector) {
    return;
  }

  schedulePreviewMessage(
    frame,
    {
      type: "scroll-to-element",
      selector,
    },
    IMMEDIATE_MESSAGE_DELAYS_MS,
  );
}

export function navigatePreviewToRoute(
  frame: HTMLIFrameElement | null | undefined,
  routeUrl: string,
  selector?: string | null,
): void {
  if (!frame) {
    return;
  }

  markPreviewFramePending(frame);

  if (selector) {
    const handleLoad = () => {
      schedulePreviewMessage(
        frame,
        {
          type: "scroll-to-element",
          selector,
        },
        LOAD_MESSAGE_DELAYS_MS,
      );
      frame.removeEventListener("load", handleLoad);
    };

    frame.addEventListener("load", handleLoad);
  }

  frame.src = routeUrl;
}
